import { encodeAbiParameters, keccak256 } from 'viem';
import {
  verifiedPoolsBasicHookAddress,
  verifiedPoolsBasicHookV2Address,
  daiAddress,
  usdcAddress,
  cbethAddress,
  lsethAddress,
  eurcAddress,
  cbbtcAddress,
  ethAddress,
  tabcAddress,
  tlmnAddress,
  txyzAddress,
  tcoinAddress,
} from '@/../generated/generated';
import { getFeatureFlagsFromEnv, FEATURE_FLAGS } from '@/utils/featureFlags';

// TOKEN_CONTRACTS represents the token contracts that have been deployed on the base testnet.
export const TOKEN_CONTRACTS: Record<string, Record<string, `0x${string}`>> = {
  DAI: daiAddress,
  USDC: usdcAddress,
  cbETH: cbethAddress,
  LsETH: lsethAddress,
  EURC: eurcAddress,
  cbBTC: cbbtcAddress,
  ETH: ethAddress,
  tABC: tabcAddress,
  tLMN: tlmnAddress,
  tXYZ: txyzAddress,
  tCOIN: tcoinAddress,
};

// Currency represents a token address.
type Currency = `0x${string}`;

// IHooks represents a contract address for the hook contract.
type IHooks = `0x${string}`;

// PoolKey is a type that represents the identifier of a Pool in Uni v4.
export type PoolKey = {
  currency0: Currency;
  currency1: Currency;
  fee: number;
  tickSpacing: number;
  hooks: IHooks;
};

export type PoolKeyBytes32 = `0x${string}`;

export type PoolState = {
  sqrtPriceX96: bigint;
  tick: number;
  protocolFee: bigint;
  currentPriceScaled: bigint;
  liquidity: bigint;
};

export const getTokenAddress = (chainId: number, tokenSymbol: string): `0x${string}` => {
  return TOKEN_CONTRACTS[tokenSymbol][chainId];
};

export function getPoolId({
  currency0,
  currency1,
  fee,
  tickSpacing,
  hooks,
}: {
  currency0: `0x${string}`;
  currency1: `0x${string}`;
  fee: number;
  tickSpacing: number;
  hooks: `0x${string}`;
}): `0x${string}` {
  return keccak256(
    encodeAbiParameters(
      [
        { name: 'currency0', type: 'address' },
        { name: 'currency1', type: 'address' },
        { name: 'fee', type: 'uint24' },
        { name: 'tickSpacing', type: 'int24' },
        { name: 'hooks', type: 'address' },
      ],
      [currency0, currency1, fee, tickSpacing, hooks],
    ),
  );
}

// createPoolKey returns a pool key for the given tokens.
const createPoolKey = (
  chainId: number,
  tokens: string[],
  fee: number,
  tickSpacing: number,
  hookAddress?: `0x${string}`,
): { keyObj: PoolKey; keyBytes32: PoolKeyBytes32 } => {
  // The pool key requires the token addresses to be sorted numerically by their address.
  const tokenAddresses = tokens.map((token) => getTokenAddress(chainId, token)).sort();

  // Use provided hookAddress or default to V1 hook
  const resolvedHookAddress: `0x${string}` =
    hookAddress ??
    verifiedPoolsBasicHookAddress[chainId as keyof typeof verifiedPoolsBasicHookAddress];

  if (!resolvedHookAddress) {
    throw new Error('Cannot find hookAddress');
  }

  const keyObj = {
    currency0: tokenAddresses[0],
    currency1: tokenAddresses[1],
    fee,
    tickSpacing,
    hooks: resolvedHookAddress,
  };

  return {
    keyObj,
    keyBytes32: getPoolId(keyObj),
  };
};

// Pool represents pool details.
export type Pool = {
  id: string;
  tokens: string[];
  key: PoolKey;
  keyBytes32: `0x${string}`;
};

// Check feature flag for showing Tangor tokens (evaluated at module load time)
const featureFlags = getFeatureFlagsFromEnv();
const SHOW_TANGOR_TOKENS =
  featureFlags[FEATURE_FLAGS.NEXT_PUBLIC_FEATURE_FLAG_FRONTEND_SHOW_TANGOR_TOKENS]?.enabled ??
  false;

// POOLS represents the pools that have been created on the base-goerli testnet.
// This must contain the same data that was used to create the pools on the testnet.
// Tangor token pools are conditionally included based on feature flag.
const POOLS: Record<number, Pool[]> = {
  84532: [
    // V1 Pools
    (() => {
      const poolKey = createPoolKey(84532, ['DAI', 'USDC'], 200, 1);
      return {
        id: 'DAI-USDC',
        tokens: ['DAI', 'USDC'],
        key: poolKey.keyObj,
        keyBytes32: poolKey.keyBytes32,
      };
    })(),
    (() => {
      const poolKey = createPoolKey(84532, ['EURC', 'USDC'], 500, 10);
      return {
        id: 'EURC-USDC',
        tokens: ['EURC', 'USDC'],
        key: poolKey.keyObj,
        keyBytes32: poolKey.keyBytes32,
      };
    })(),
    (() => {
      const poolKey = createPoolKey(84532, ['ETH', 'cbETH'], 500, 10);
      return {
        id: 'ETH-cbETH',
        tokens: ['ETH', 'cbETH'],
        key: poolKey.keyObj,
        keyBytes32: poolKey.keyBytes32,
      };
    })(),
    (() => {
      const poolKey = createPoolKey(84532, ['ETH', 'USDC'], 500, 10);
      return {
        id: 'ETH-USDC',
        tokens: ['ETH', 'USDC'],
        key: poolKey.keyObj,
        keyBytes32: poolKey.keyBytes32,
      };
    })(),
    (() => {
      const poolKey = createPoolKey(84532, ['cbBTC', 'USDC'], 500, 50);
      return {
        id: 'cbBTC-USDC',
        tokens: ['cbBTC', 'USDC'],
        key: poolKey.keyObj,
        keyBytes32: poolKey.keyBytes32,
      };
    })(),
    // V2 Pools (tEquity pools using V2 hook) - only included if feature flag is enabled
    ...(SHOW_TANGOR_TOKENS
      ? [
          (() => {
            const v2Hook = verifiedPoolsBasicHookV2Address[84532];
            const poolKey = createPoolKey(84532, ['tABC', 'USDC'], 3000, 50, v2Hook);
            return {
              id: 'tABC-USDC',
              tokens: ['tABC', 'USDC'],
              key: poolKey.keyObj,
              keyBytes32: poolKey.keyBytes32,
            };
          })(),
          (() => {
            const v2Hook = verifiedPoolsBasicHookV2Address[84532];
            const poolKey = createPoolKey(84532, ['tLMN', 'USDC'], 3000, 50, v2Hook);
            return {
              id: 'tLMN-USDC',
              tokens: ['tLMN', 'USDC'],
              key: poolKey.keyObj,
              keyBytes32: poolKey.keyBytes32,
            };
          })(),
          (() => {
            const v2Hook = verifiedPoolsBasicHookV2Address[84532];
            const poolKey = createPoolKey(84532, ['tXYZ', 'USDC'], 3000, 50, v2Hook);
            return {
              id: 'tXYZ-USDC',
              tokens: ['tXYZ', 'USDC'],
              key: poolKey.keyObj,
              keyBytes32: poolKey.keyBytes32,
            };
          })(),
          (() => {
            const v2Hook = verifiedPoolsBasicHookV2Address[84532];
            const poolKey = createPoolKey(84532, ['tCOIN', 'USDC'], 3000, 50, v2Hook);
            return {
              id: 'tCOIN-USDC',
              tokens: ['tCOIN', 'USDC'],
              key: poolKey.keyObj,
              keyBytes32: poolKey.keyBytes32,
            };
          })(),
        ]
      : []),
  ],
  // reference:
  // https://github.cbhq.net/smart-contracts/clear-pools/blob/master/terraform/prod/20250312120752_deploy_launch_pools.tf
  8453: [
    (() => {
      const poolKey = createPoolKey(8453, ['EURC', 'USDC'], 200, 50);
      return {
        id: 'EURC-USDC',
        tokens: ['EURC', 'USDC'],
        key: poolKey.keyObj,
        keyBytes32: poolKey.keyBytes32,
      };
    })(),
    (() => {
      const poolKey = createPoolKey(8453, ['ETH', 'USDC'], 1500, 200);
      return {
        id: 'ETH-USDC',
        tokens: ['ETH', 'USDC'],
        key: poolKey.keyObj,
        keyBytes32: poolKey.keyBytes32,
      };
    })(),
    (() => {
      const poolKey = createPoolKey(8453, ['ETH', 'cbBTC'], 3000, 300);
      return {
        id: 'ETH-cbBTC',
        tokens: ['ETH', 'cbBTC'],
        key: poolKey.keyObj,
        keyBytes32: poolKey.keyBytes32,
      };
    })(),
    (() => {
      const poolKey = createPoolKey(8453, ['cbBTC', 'USDC'], 3000, 200);
      return {
        id: 'cbBTC-USDC',
        tokens: ['cbBTC', 'USDC'],
        key: poolKey.keyObj,
        keyBytes32: poolKey.keyBytes32,
      };
    })(),
    (() => {
      const poolKey = createPoolKey(8453, ['ETH', 'cbETH'], 500, 20);
      return {
        id: 'ETH-cbETH',
        tokens: ['ETH', 'cbETH'],
        key: poolKey.keyObj,
        keyBytes32: poolKey.keyBytes32,
      };
    })(),
  ],
};

const POOLS_MAP: Record<number, Record<string, Pool>> = {};
for (const chainId in POOLS) {
  POOLS_MAP[chainId] = POOLS[chainId].reduce<Record<string, Pool>>((acc, pool) => {
    // Sort the tokens and use the sorted tokens as the key in the map.
    const tokenPair = pool.tokens.slice().sort();
    acc[tokenPair.toString()] = pool;
    return acc;
  }, {});
}

// getPools returns a list of pools that have been created.
// Tangor token pools are already filtered based on feature flag in POOLS definition.
export const getPools = (chainId: number): Pool[] => {
  return POOLS[chainId];
};

// getPool returns the pool for the given token pair.
export const getPool = (chainId: number, tokenA: string, tokenB: string): Pool => {
  // Sort the tokens and use the sorted tokens as the key
  const tokenPair = [tokenA, tokenB].slice().sort();
  return POOLS_MAP[chainId][tokenPair.toString()];
};

export const getPoolByKeyBytes32 = ({
  chainId,
  poolId,
}: {
  chainId?: number;
  poolId: string;
}): { pool: Pool; chainId: number } => {
  // If a specific chainId is provided, search only on that chain.
  if (typeof chainId === 'number') {
    const poolsOnChain = getPools(chainId);
    const foundPool = poolsOnChain.find((pool) => pool.keyBytes32 === poolId);

    if (foundPool) {
      return { pool: foundPool, chainId: chainId };
    }
    // If not found on the specified chain, the function will fall through
    // and throw an error at the end, consistent with original behavior.
  } else {
    // If no chainId is provided, iterate through all chains defined in POOLS_MAP.
    for (const chainIdStr in POOLS_MAP) {
      // Assuming chainIdStr from POOLS_MAP keys are valid numeric chain IDs.
      const numericChainId = Number(chainIdStr);
      const poolsOnChain = getPools(numericChainId);
      const foundPool = poolsOnChain.find((pool) => pool.keyBytes32 === poolId);

      if (foundPool) {
        return { pool: foundPool, chainId: numericChainId };
      }
    }
  }

  // If the pool was not found (either on a specific chain or across all chains),
  // throw an error.
  throw new Error('Cannot find the pool using keyBytes32');
};

export const getZeroForOne = (chainId: number, tokenA: string, tokenB: string): boolean => {
  const tokenAAddr = getTokenAddress(chainId, tokenA).toLowerCase();
  const tokenBAddr = getTokenAddress(chainId, tokenB).toLowerCase();
  return tokenAAddr < tokenBAddr;
};

export const DEFAULT_POOL_KEY_FEE = 200;
export const DEFAULT_POOL_KEY_TICK_SPACING = 1;
